import React, { useState } from "react";
import * as api from "../services/api";
import { useLoaderData, useRevalidator, useSearchParams } from "react-router";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import * as f from "../utils/formatter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faChartSimple,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { currentLocationFromRequest } from "../components/LocationSelector";
import * as calUtil from "shared/src/calendar.mjs";
import * as d from "shared/src/date.mjs";
import ButtonLoader from "../components/ButtonLoader";
import { MoreVert, Visibility, VisibilityOff } from "@mui/icons-material";
import DatePicker from "../components/mui/DatePicker";
import { errorsFromApi, formData, FormErrorList, useFormAction } from "../form";
import { compose, updateIn } from "shared/src/util.mjs";
import TimeRangePicker from "../components/mui/TimeRangePicker";
import { faChairOffice } from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { msToM } from "../components/resource-list/fn.mjs";
import { role } from "shared/src/roles.mjs";
import AutocompleteSelect from "../components/AutocompleteSelect";
import { useSnackbarMessage } from "../components/Snackbar.jsx";
import { employeeStatus } from "shared/src/employee.mjs";

/**
 * @typedef Shift
 * @property {string} from
 * @property {string} to
 *
 * @typedef Break
 * @property {String} from
 * @property {String} to
 *
 * @typedef TimeSpent
 * @property {Date} from
 * @property {Date} to
 *
 * @typedef Employee
 * @property {string} id
 * @property {string} first_name
 * @property {string} last_name
 * @property {TimeSpent[]} times_spent
 * @property {Break[]} breaks
 * @property {Shift} [shift]
 *
 * @typedef {Pick<import('shared/src/gql-types.mjs').Location, 'id' | 'working_hours'>} Location
 */

const interestingRoles = [
  role.Dental_Assistant,
  role.Floor_Manager,
  role.Treatment_Coordinator,
  role.Doctor,
];

function useChangeQuery() {
  const [_, setSearchParams] = useSearchParams();
  const changeQuery = React.useCallback(
    (newValues) => {
      setSearchParams((prev) => ({
        ...Object.fromEntries(prev),
        ...newValues,
      }));
    },
    [setSearchParams],
  );

  return changeQuery;
}

export async function loader({ request }) {
  const url = new URL(request.url);
  const visibleRoles = visibleRolesFromSearchParams(url.searchParams);

  const now = new Date();
  const location_id = currentLocationFromRequest(request);

  if (location_id == null) {
    return {
      now,
      data: {
        location: null,
        employees: { data: [] },
        appointments: { data: [] },
      },
    };
  }

  const res = await api.loadFloorManagerEmployees(
    {
      location_id: location_id,
      day: d.toDateString(now),
      now: d.toString(now),
      employeeFilter: {
        shift: {
          from: {
            gte: d.toString(d.startOfDay(now)),
            lt: d.toString(d.endOfDay(now)),
          },
        },
        or: visibleRoles.map((role) => ({ roles: { location_id, role } })),
      },
    },
    api.contextFromRequest(request),
  );

  return { ...res, now };
}

/**
 * @param {string} [timeStr]
 *
 * @returns {number[]?}
 */
function parseTime(timeStr) {
  const parts = timeStr?.split(":").map((v) => parseInt(v, 10));

  if (
    parts == null ||
    parts.length !== 2 ||
    parts.some((p) => Number.isNaN(p))
  ) {
    return null;
  }

  return parts;
}

/**
 * @param {string} [timeStr]
 *
 * @returns {number?}
 */
function createFromHour(timeStr) {
  const time = parseTime(timeStr);

  return time?.[0];
}

/**
 * @param {string} [timeStr]
 *
 * @returns {number?}
 */
function createToHour(timeStr) {
  const time = parseTime(timeStr);
  if (time == null) {
    return;
  }

  const [h, m] = time;

  return m > 0 ? h + 1 : h;
}

/**
 * @param {object} wh
 * @param {string} wh.from
 * @param {string} wh.to
 */
function* hours(wh) {
  const from = createFromHour(wh?.from);
  const to = createToHour(wh?.to);
  if (from == null || to == null) {
    return;
  }

  for (let i = from; i <= to; i++) {
    yield i;
  }
}

/**
 * @param {Date} date
 */
function dateToHours(date) {
  return date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
}

/**
 * @param {number} ms
 */
function msToHours(ms) {
  return ms / 3600000;
}

/**
 * @param {Location} [location]
 * @param {Date} now
 */
function hoursFromLocation(location, now) {
  const day = calUtil.dateToDay(now);
  const wh = calUtil.workingHoursInDay(now, location?.working_hours?.[day]);
  return Array.from(hours(wh));
}

/**
 * @param {number} h
 */
function formatHour(h) {
  return String(h).padStart(2, "0") + ":00";
}

/**
 * @param {object} props
 * @param {() => void} props.close
 * @param {Employee[]} props.employees
 * @param {Date} props.now
 */
function EmployeeChartDialog({ close, employees, location, now }) {
  const hs = hoursFromLocation(location, now);

  if (hs.length === 0) {
    return null;
  }

  const firstHour = hs[0];
  const hourSize = 100 / hs.length;

  return (
    <Dialog open={true} onClose={() => close()} fullWidth maxWidth="xxl">
      <DialogTitle>
        Všechny sestry na směně
        <IconButton aria-label="close" onClick={() => close()}>
          <FontAwesomeIcon icon={faXmark} fixedWidth />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table
            sx={{
              "& .MuiTableCell-root": {
                borderBottom: "none",
              },
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell></TableCell>
                <TableCell sx={{ width: "90%" }}>
                  <Box display="flex">
                    {hs.map((h) => (
                      <Box
                        key={h}
                        sx={{
                          pt: 1,
                          px: 1,
                          width: 0,
                          flexGrow: 1,
                          borderBottom: "1px solid #B2C6DE",
                          borderRight: "1px solid #B2C6DE",
                          "&:last-child": {
                            borderRight: "none",
                          },
                        }}
                      >
                        {formatHour(h)}
                      </Box>
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
              {employees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>{f.fullName(employee)}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        position: "relative",
                        paddingBottom: 1,
                        paddingTop: 4,
                      }}
                    >
                      <Box
                        sx={(theme) => ({
                          position: "absolute",
                          top: 0,
                          left: `${(dateToHours(new Date(employee.shift.from)) - firstHour) * hourSize}%`,
                          width: `${msToHours(new Date(employee.shift.to) - new Date(employee.shift.from)) * hourSize}%`,
                          height: theme.spacing(4),
                          backgroundColor: "background.default",
                          borderRadius: 0.5,
                        })}
                      />
                      {employee.times_spent?.map((ts) => {
                        const from = d.parseUtcDateTime(ts.from);
                        const to = d.parseUtcDateTime(ts.to);

                        return (
                          <Tooltip
                            title="na křesle"
                            placement="top"
                            arrow="true"
                            key={ts.from}
                          >
                            <Box
                              sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "absolute",
                                top: 0,
                                overflow: "hidden",
                                textAlign: "center",
                                left: `${(dateToHours(from) - firstHour) * hourSize}%`,
                                width: `${msToHours(to - from) * hourSize}%`,
                                height: theme.spacing(4),
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: 0.5,
                              })}
                            >
                              <FontAwesomeIcon
                                color={"white"}
                                icon={faChairOffice}
                              />
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}

/**
 * @param {object} props
 * @param {Location} props.location
 * @param {Employee[]} props.employees
 * @param {Date} props.now
 */
function EmployeeChart({ location, employees, now }) {
  const [showDialog, setShowDialog] = React.useState(false);

  return (
    <>
      <IconButton
        onClick={() => setShowDialog(true)}
        sx={{ aspectRatio: "1 / 1" }}
        size="small"
      >
        <FontAwesomeIcon icon={faChartSimple} fixedWidth />
      </IconButton>
      {showDialog && (
        <EmployeeChartDialog
          close={() => setShowDialog(false)}
          employees={employees}
          location={location}
          now={now}
        />
      )}
    </>
  );
}

function formConverter(formData) {
  const { day, ...rest } = formData;

  return compose(
    (m) => updateIn(m, ["from"], (v) => `${day} ${v}`),
    (m) => updateIn(m, ["to"], (v) => `${day} ${v}`),
  )(rest);
}

/**
 * @param {object} props
 * @param {Employee} props.employee
 */
function AddBreakDialog({ close, employee }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const displaySnackbarMessage = useSnackbarMessage();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.createEmployeeBreak(formConverter(formData(e.currentTarget))),
    () => {
      revalidator.revalidate();
      displaySnackbarMessage({
        title: t("Pauza byla úspěšně přidána."),
      });
      close();
    },
  );

  return (
    <Dialog open onClose={() => close()}>
      <DialogTitle>Přidat pauzu ({f.fullName(employee)})</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <input type="hidden" name="employee_id" value={employee.id} />
          <DatePicker
            label="Den"
            name="day"
            value={d.toDateString(new Date())}
            readOnly
          />

          <TimeRangePicker
            autoFocus
            nameFrom="from"
            nameTo="to"
            slotProps={{
              textField: {
                label: "Od - do",
                required: true,
              },
            }}
          />

          <FormErrorList formErrors={errors} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => close()}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function FormatBreak({ employee, b }) {
  const fromS = f.time24(b.from);
  const toS = f.time24(b.to);
  const displaySnackbarMessage = useSnackbarMessage();

  return (
    <span>
      {fromS} - {toS}
      <ActionIconButton
        icon={faTrash}
        action={() => {
          api.deleteEmployeeBreak({
            from: b.from,
            to: b.to,
            employee_id: employee.id,
          });
          displaySnackbarMessage({
            title: "Pauza byla úspěšně zrušena",
          });
        }}
      />
    </span>
  );
}

function getEmployeeTimeSpent(employee) {
  const ms = employee.times_spent.reduce((acc, ts) => {
    const from = d.parseUtcDateTime(ts.from);
    const to = d.parseUtcDateTime(ts.to);

    return acc + (to - from);
  }, 0);

  return msToM(ms);
}

function EmployeeRoles({ employee, location }) {
  const { t } = useTranslation();

  const roles = employee.roles?.[location.id];
  if (roles == null) {
    return null;
  }

  return roles
    .map((r) => t(`roles.${r}`))
    .sort()
    .join(", ");
}

const employeeStatusLabel = {
  [employeeStatus.ASSIGNED]: "Přiřazený",
  [employeeStatus.AVAILABLE]: "Volný",
  [employeeStatus.UNAVAILABLE]: "Nepřítomný",
};

/**
 * @param {object} props
 * @param {Employee} props.employee
 * @param {Date} now
 * @param {Location?} props.location
 */
function EmployeeRow({ employee, now, location }) {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [employeeBreak, setEmployeeBreak] = useState(false);

  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const { t } = useTranslation();
  const displaySnackbarMessage = useSnackbarMessage();

  const hs = hoursFromLocation(location, now);
  const firstHour = hs[0];
  const hourSize = 100 / hs.length;

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleOpenPinDialog = () => {
    handleMenuClose();
    setPinDialogOpen(true);
  };
  const isPinValid = /^\d{6}$/.test(newPin);

  const handleClosePinDialog = () => {
    setPinDialogOpen(false);
    setNewPin("");
    setConfirmPin("");
    setPinError("");
  };
  const handleChangePin = async () => {
    if (!/^\d{6}$/.test(newPin)) {
      setPinError(t("employees.pinValidationError"));
      return;
    }

    if (newPin !== confirmPin) {
      setPinError(t("Potvrzení PINu se neshoduje se nově zadaný PINem"));
      return;
    }

    setPinError("");

    try {
      setIsSaving(true);

      const response = await api.updateEmployeePin(employee.id, newPin);

      if (response.data?.changeEmployeePin) {
        displaySnackbarMessage({
          title: t("Změna PINu proběhla úspěšně."),
        });
        handleClosePinDialog();
        setNewPin("");
      } else {
        setPinError(t("employees.pinUpdateFailed"));
      }
    } catch (err) {
      console.error("PIN update failed:", err);
      setPinError(t("employees.networkError"));
    } finally {
      setIsSaving(false);
    }
  };

  const [break1, ...restBreaks] = employee.breaks.map((b) => (
    <FormatBreak key={`${b.from}.${b.to}`} employee={employee} b={b} />
  ));

  return (
    <>
      <TableRow sx={{ "& .MuiTableCell-body": { borderBottom: "none" } }}>
        <TableCell>{f.fullName(employee)}</TableCell>
        <TableCell>{f.time24(employee.shift?.from)}</TableCell>
        <TableCell>{f.time24(employee.shift?.to)}</TableCell>
        <TableCell>
          {f.minutesToDuration(getEmployeeTimeSpent(employee))}
        </TableCell>
        <TableCell>{break1}</TableCell>
        <TableCell>
          <Box display="flex" flexDirection="column">
            {restBreaks}
          </Box>
        </TableCell>
        <TableCell>
          {employeeStatusLabel[employee.status] ?? employee.status}
        </TableCell>
        <TableCell>
          <EmployeeRoles employee={employee} location={location} />
        </TableCell>
        <TableCell>
          <IconButton
            onClick={() => setOpen((open) => !open)}
            size="small"
            sx={{ aspectRatio: "1 / 1" }}
          >
            <FontAwesomeIcon icon={faChartSimple} fixedWidth />
          </IconButton>
        </TableCell>
        <TableCell>
          <IconButton onClick={handleMenuOpen} size="small">
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
          >
            <MenuItem
              onClick={() => setEmployeeBreak(true)}
              disabled={employee.breaks.length >= 2}
            >
              Přidat pauzu
            </MenuItem>
            <MenuItem onClick={handleOpenPinDialog}>
              {t("employees.changePIN")}
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ p: 0 }}>
          <Collapse in={open}>
            <Box sx={{ position: "relative", paddingBottom: 1, paddingTop: 5 }}>
              <Box
                sx={(theme) => ({
                  position: "absolute",
                  top: 0,
                  left: `${(dateToHours(new Date(employee.shift.from)) - firstHour) * hourSize}%`,
                  width: `${msToHours(new Date(employee.shift.to) - new Date(employee.shift.from)) * hourSize}%`,
                  height: theme.spacing(4),
                  backgroundColor: "background.default",
                  borderRadius: 0.5,
                })}
              />
              {employee.times_spent?.map((ts) => {
                const from = d.parseUtcDateTime(ts.from);
                const to = d.parseUtcDateTime(ts.to);

                return (
                  <Tooltip
                    title="na křesle"
                    placement="top"
                    arrow="true"
                    key={ts.from}
                  >
                    <Box
                      sx={(theme) => ({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "absolute",
                        top: 0,
                        overflow: "hidden",
                        textAlign: "center",
                        left: `${(dateToHours(from) - firstHour) * hourSize}%`,
                        width: `${msToHours(to - from) * hourSize}%`,
                        height: theme.spacing(4),
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: 0.5,
                      })}
                    >
                      <FontAwesomeIcon color={"white"} icon={faChairOffice} />
                    </Box>
                  </Tooltip>
                );
              })}

              <Box display="flex">
                {hs.map((h) => (
                  <Box
                    key={h}
                    sx={{
                      pt: 1,
                      px: 1,
                      width: 0,
                      flexGrow: 1,
                      borderTop: "1px solid #B2C6DE",
                      borderRight: "1px solid #B2C6DE",
                      "&:last-child": {
                        borderRight: "none",
                      },
                    }}
                  >
                    {formatHour(h)}
                  </Box>
                ))}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      {employeeBreak && (
        <AddBreakDialog
          employee={employee}
          close={() => setEmployeeBreak(false)}
        />
      )}
      <Dialog open={pinDialogOpen} onClose={handleClosePinDialog}>
        <DialogTitle>
          {t("employees.changePINFor", {
            name: f.fullName(employee),
          })}
          <IconButton aria-label="close" onClick={handleClosePinDialog}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleChangePin();
            }}
          >
            <TextField
              label={t("employees.newPIN")}
              type={showPin ? "text" : "password"}
              variant="outlined"
              value={newPin}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, "");
                setNewPin(digitsOnly);
                if (pinError) setPinError("");
              }}
              inputMode="numeric"
              fullWidth
              autoFocus
              margin="normal"
              error={!!pinError}
              helperText={pinError}
              slotProps={{
                htmlInput: {
                  maxLength: 6,
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPin(!showPin)}
                        edge="end"
                      >
                        {showPin ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              label={t("employees.confirmPIN")}
              type={showPin ? "text" : "password"}
              variant="outlined"
              value={confirmPin}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, "");
                setConfirmPin(digitsOnly);
                if (pinError) setPinError("");
              }}
              inputMode="numeric"
              fullWidth
              margin="normal"
              error={!!pinError}
              helperText={pinError}
              slotProps={{
                htmlInput: {
                  maxLength: 6,
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPin(!showPin)}
                        edge="end"
                      >
                        {showPin ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <DialogActions sx={{ padding: 0, marginTop: 1 }}>
              <Button variant="outlined" onClick={handleClosePinDialog}>
                {t("employees.buttons.cancel")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSaving || !isPinValid}
              >
                {isSaving
                  ? t("employees.buttons.saving")
                  : t("employees.buttons.save")}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ActionButton({ children, action }) {
  const [loading, setLoading] = React.useState(false);
  const revalidator = useRevalidator();

  return (
    <Button
      onClick={async () => {
        try {
          setLoading(true);
          await action();
          revalidator.revalidate();
        } finally {
          setLoading(false);
        }
      }}
      disabled={loading}
    >
      {loading && <ButtonLoader />}
      {children}
    </Button>
  );
}

function ActionIconButton({ icon, action }) {
  const [loading, setLoading] = React.useState(false);
  const revalidator = useRevalidator();

  return (
    <IconButton
      onClick={async () => {
        try {
          setLoading(true);
          await action();
          revalidator.revalidate();
        } finally {
          setLoading(false);
        }
      }}
      disabled={loading}
    >
      {loading && <ButtonLoader />}
      <FontAwesomeIcon icon={icon} />
    </IconButton>
  );
}

function RefreshShiftsButton() {
  const { t } = useTranslation();
  const displaySnackbarMessage = useSnackbarMessage();

  return (
    <ActionButton
      action={async () => {
        const result = await api.syncEmployeeShifts();
        const errors = errorsFromApi({ result, t });
        if (errors.length === 0) {
          displaySnackbarMessage({ content: "Směny načteny." });
        } else {
          displaySnackbarMessage({
            title: "Nepodařilo se načíst směny.",
            content: errors.map((error, i) => <p key={i}>{error}</p>),
            severity: "error",
          });
        }
      }}
    >
      <FontAwesomeIcon icon={faArrowsRotate} />
      &nbsp; Směny
    </ActionButton>
  );
}

function RefreshAvailabilityButton() {
  const { t } = useTranslation();
  const displaySnackbarMessage = useSnackbarMessage();

  return (
    <ActionButton
      action={async () => {
        const result = await api.syncAvailableEmployees();
        const errors = errorsFromApi({ result, t });
        if (errors.length === 0) {
          displaySnackbarMessage({ content: "Stavy načteny." });
        } else {
          displaySnackbarMessage({
            title: "Nepodařilo se načíst stavy.",
            content: errors.map((error, i) => <p key={i}>{error}</p>),
            severity: "error",
          });
        }
      }}
    >
      <FontAwesomeIcon icon={faArrowsRotate} />
      &nbsp; Stav
    </ActionButton>
  );
}

function RefreshRolesButton() {
  const { t } = useTranslation();
  const displaySnackbarMessage = useSnackbarMessage();

  return (
    <ActionButton
      action={async () => {
        const result = await api.syncExistingEmployees();
        const errors = errorsFromApi({ result, t });
        if (errors.length === 0) {
          displaySnackbarMessage({ content: "Role načteny." });
        } else {
          displaySnackbarMessage({
            title: "Nepodařilo se načíst role.",
            content: errors.map((error, i) => <p key={i}>{error}</p>),
            severity: "error",
          });
        }
      }}
    >
      <FontAwesomeIcon icon={faArrowsRotate} />
      &nbsp; Role
    </ActionButton>
  );
}

function visibleRolesFromSearchParams(searchParams) {
  const roles = searchParams.getAll("roles");

  return roles.length === 0 ? interestingRoles : roles;
}

function useVisibleRoles() {
  const [searchParams] = useSearchParams();

  return visibleRolesFromSearchParams(searchParams);
}

export default function Employees() {
  const { t } = useTranslation();
  const loaderData = useLoaderData();
  const employeesConnection = loaderData.data.employees;
  const location = loaderData.data.location;
  const now = loaderData.now;
  const roleOptions = React.useMemo(
    () =>
      interestingRoles
        .map((r) => ({ id: r, name: t(`roles.${r}`) }))
        .toSorted((o1, o2) => o1.name.localeCompare(o2.name)),
    [t],
  );
  const changeQuery = useChangeQuery();
  const visibleRoles = useVisibleRoles();
  const visibleRoleOptions = React.useMemo(() => {
    const set = new Set(visibleRoles);

    return roleOptions.filter((o) => set.has(o.id));
  }, [visibleRoles]);

  return (
    <>
      <Typography variant="h1" sx={{ mb: 2 }}>
        Personál
      </Typography>
      <Card>
        <CardContent>
          <Box display="flex" marginBottom={2}>
            <EmployeeChart
              location={location}
              employees={employeesConnection.data}
              now={now}
            />
            <RefreshRolesButton />
            <RefreshShiftsButton />
            <RefreshAvailabilityButton />
            <Box sx={{ flexGrow: 1 }}></Box>
            <AutocompleteSelect
              options={roleOptions}
              value={visibleRoleOptions}
              multiple
              onChange={(opts) => {
                changeQuery({ roles: opts.map((o) => o.id) });
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: "70vh" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("employees.fullName")}</TableCell>
                  <TableCell>Plánovaný příchod</TableCell>
                  <TableCell>Plánovaný odchod</TableCell>
                  <TableCell>Index čerstvosti</TableCell>
                  <TableCell>Plánovaná pauza 1</TableCell>
                  <TableCell>Plánovaná pauza 2</TableCell>
                  <TableCell>Stav</TableCell>
                  <TableCell>Pozice</TableCell>
                  <TableCell>Graf</TableCell>
                  <TableCell>{t("employees.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employeesConnection.data.map((row) => (
                  <EmployeeRow
                    key={row.id}
                    employee={row}
                    now={now}
                    location={location}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
}
